import type { Variants } from "motion/react";
import { useAnimation } from "motion/react";
import * as m from "motion/react-m";
import { forwardRef, useImperativeHandle } from "react";

import { cn } from "@/shared/lib";
import type { AnimatedIconProps } from "@/shared/ui/animated-icon";

export interface LayoutGridIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

const RECT_1_VARIANTS: Variants = {
  normal: { translateX: 0, translateY: 0 },
  animate: {
    translateX: [0, 11, 11, 0],
    translateY: [0, 0, 0, 0],
    transition: { duration: 0.8, ease: "easeInOut", times: [0, 0.4, 0.6, 1] },
  },
};

const RECT_2_VARIANTS: Variants = {
  normal: { translateX: 0, translateY: 0 },
  animate: {
    translateX: [0, 0, 0, 0],
    translateY: [0, 11, 11, 0],
    transition: { duration: 0.8, ease: "easeInOut", times: [0, 0.4, 0.6, 1] },
  },
};

const RECT_3_VARIANTS: Variants = {
  normal: { translateX: 0, translateY: 0 },
  animate: {
    translateX: [0, -11, -11, 0],
    translateY: [0, 0, 0, 0],
    transition: { duration: 0.8, ease: "easeInOut", times: [0, 0.4, 0.6, 1] },
  },
};

const RECT_4_VARIANTS: Variants = {
  normal: { translateX: 0, translateY: 0 },
  animate: {
    translateX: [0, 0, 0, 0],
    translateY: [0, -11, -11, 0],
    transition: { duration: 0.8, ease: "easeInOut", times: [0, 0.4, 0.6, 1] },
  },
};

const LayoutGridIcon = forwardRef<LayoutGridIconHandle, AnimatedIconProps>(
  ({ className, size = 24, ...props }, ref) => {
    const controls = useAnimation();
    useImperativeHandle(ref, () => {
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    return (
      <svg
        className={cn("lucide", className)}
        {...props}
        fill="none"
        height={size}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        width={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <m.rect
          animate={controls}
          height="7"
          initial="normal"
          rx="1"
          variants={RECT_1_VARIANTS}
          width="7"
          x="3"
          y="3"
        />
        <m.rect
          animate={controls}
          height="7"
          initial="normal"
          rx="1"
          variants={RECT_2_VARIANTS}
          width="7"
          x="14"
          y="3"
        />
        <m.rect
          animate={controls}
          height="7"
          initial="normal"
          rx="1"
          variants={RECT_3_VARIANTS}
          width="7"
          x="14"
          y="14"
        />
        <m.rect
          animate={controls}
          height="7"
          initial="normal"
          rx="1"
          variants={RECT_4_VARIANTS}
          width="7"
          x="3"
          y="14"
        />
      </svg>
    );
  }
);

LayoutGridIcon.displayName = "LayoutGridIcon";

export { LayoutGridIcon };
